import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import {
  errorCodeSelector,
  optionsSelector,
  userSelector,
  usersSelector,
  companiesSelector,
} from './store/selector';
import { usersApiService } from './services/api';
import {
  userClearAction,
  getUserFailureApiAction,
  getUserInitApiAction,
  getUserSuccessApiAction,
  getUsersFailureApiAction,
  getUsersInitApiAction,
  getUsersSuccessApiAction,
  putUserFailuireApiAction,
  putUserInitApiAction,
  putUserSuccessApiAction,
  setOptionsAction,
  putBlockUserInitApiAction,
  putBlockUserSuccessApiAction,
  putBlockUserFailuireApiAction,
  putUnblockUserInitApiAction,
  putUnblockUserSuccessApiAction,
  putUnblockUserFailuireApiAction,
  resetOptionsAction,
  postUserInitApiAction,
  postUserSuccessApiAction,
  postUserFailuireApiAction,
  getCompaniesInitApiAction,
  getCompaniesSuccessApiAction,
  getCompaniesFailureApiAction,
  deleteUserInitApiAction,
  deleteUserSuccessApiAction,
  deleteUserFailureApiAction,
} from './store/reducer';
import { GetUsersParams } from './interfaces/get-users';
import { GetUserParams } from './interfaces/get-user';
import { PostUserPayloadRequest } from './interfaces/post-user';
import { PutUserPayload } from './interfaces/put-user';

export function useUserCore() {
  const users = useSelector(usersSelector);
  const user = useSelector(userSelector);
  const options = useSelector(optionsSelector);
  const companies = useSelector(companiesSelector);
  const errorCode = useSelector(errorCodeSelector);
  const dispatch = useDispatch();

  const getUsers = useCallback(
    async (params: GetUsersParams, onSuccess?: () => void) => {
      try {
        dispatch(getUsersInitApiAction());
        const response = await usersApiService.getUsers(params);
        dispatch(getUsersSuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        dispatch(getUsersFailureApiAction({ errorCode: error.request.status }));
      }
    },
    [dispatch]
  );
 const getCompanyUsers = useCallback(
    async (companyId: string) => {
      try {
        dispatch(getUsersInitApiAction());
        // Llama al nuevo método del servicio de API
        const response = await usersApiService.getCompanyUsers(companyId);
        dispatch(getUsersSuccessApiAction(response));
      } catch (error: any) {
        console.error(error);
        dispatch(getUsersFailureApiAction({ errorCode: error.request.status }));
      }
    },
    [dispatch]
  );
  const deleteUser = useCallback(
    async (
      userId: string,
      onSuccess?: () => void,
      onFailure?: (error: any) => void
    ) => {
      try {
        dispatch(deleteUserInitApiAction());
        await usersApiService.deleteUser(userId);
        dispatch(deleteUserSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error("Error al eliminar usuario:", error);
        onFailure?.(error);
        dispatch(deleteUserFailureApiAction({ errorCode: error.response?.status || 500 }));
      }
    },
    [dispatch]
  );

  const getUser = useCallback(
    async (id: string, onSuccess?: () => void) => {
      try {
        dispatch(getUserInitApiAction());
        const response = await usersApiService.getUser(id);
        dispatch(getUserSuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        dispatch(getUserFailureApiAction({ errorCode: error.request.status }));
      }
    },
    [dispatch]
  );

  const postUser = useCallback(
    async (
      body: PostUserPayloadRequest,
      onSuccess?: () => void,
      onFailure?: (error: any) => void
    ) => {
      try {
        dispatch(postUserInitApiAction());
        await usersApiService.postUser(body);
        dispatch(postUserSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        onFailure?.(error);
        dispatch(postUserFailuireApiAction({ errorCode: error.response?.status || 500 }));
      }
    },
    [dispatch]
  );

  const putUser = useCallback(
    async (
      id: string,
      body: PutUserPayload,
      onSuccess?: () => void,
      onFailure?: (error: any) => void 
    ) => {
      try {
        dispatch(putUserInitApiAction());
        await usersApiService.putUser(id, body);
        dispatch(putUserSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        
        onFailure?.(error); 
        
        dispatch(putUserFailuireApiAction());
      }
    },
    [dispatch]
  );

  const getUserByEmail = useCallback(
    async (params: GetUserParams, onSuccess?: () => void) => {
      try {
        dispatch(getUserInitApiAction());
        const response = await usersApiService.getUserByEmail(params);
        dispatch(getUserSuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        dispatch(getUserFailureApiAction({ errorCode: error.request.status }));
      }
    },
    [dispatch]
  );

  const putBlockUser = useCallback(
    async (id: string, onSuccess?: () => void) => {
      try {
        dispatch(putBlockUserInitApiAction());
        await usersApiService.putBlockUser(id);
        dispatch(putBlockUserSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        dispatch(putBlockUserFailuireApiAction());
      }
    },
    [dispatch]
  );

  const putUnblockUser = useCallback(
    async (id: string, onSuccess?: () => void) => {
      try {
        dispatch(putUnblockUserInitApiAction());
        await usersApiService.putUnblockUser(id);
        dispatch(putUnblockUserSuccessApiAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        dispatch(putUnblockUserFailuireApiAction());
      }
    },
    [dispatch]
  );

  const getCompanies = useCallback(
    async (id: string, onSuccess?: () => void) => {
      try {
        dispatch(getCompaniesInitApiAction());
        const response = await usersApiService.getCompanies(id);
        dispatch(getCompaniesSuccessApiAction(response));
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        dispatch(
          getCompaniesFailureApiAction({ errorCode: error.request.status })
        );
      }
    },
    [dispatch]
  );

  const setOptions = useCallback((options: GetUsersParams) => {
    dispatch(setOptionsAction(options));
  }, [dispatch]);

  const resetOptions = useCallback(() => {
    dispatch(resetOptionsAction());
  }, [dispatch]);

  const clearUser = useCallback(() => {
    dispatch(userClearAction());
  }, [dispatch]);

  return {
    users,
    user,
    options,
    companies,
    errorCode,
    getUsers,
    getUser,
    getUserByEmail,
    postUser,
    putUser,
    putBlockUser,
    putUnblockUser,
    getCompanies,
    setOptions,
    resetOptions,
    clearUser,
    getCompanyUsers,
    deleteUser
  };
}
